import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api.service';
import { useTenantStore } from '../stores/tenant.store';

export function BranchesAndCash() {
  const contextLegalEntityId = useTenantStore((s) => s.legalEntityId);
  const [branches, setBranches] = useState<any[]>([]);
  const [registers, setRegisters] = useState<any[]>([]);
  const [entities, setEntities] = useState<any[]>([]);
  const [pendingAuditsCount, setPendingAuditsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [branchForm, setBranchForm] = useState({ code: '', name: '', zone: '', legalEntityId: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [b, r, pending, le] = await Promise.all([
        apiService.getBranches(),
        apiService.getCashRegisters(),
        apiService.listPendingAuditSessions().catch(() => []),
        apiService.listLegalEntities(),
      ]);
      setBranches(b);
      setRegisters(r);
      setPendingAuditsCount((pending ?? []).length);
      setEntities(le ?? []);
      if (!branchForm.legalEntityId) {
        const defaultLe = contextLegalEntityId || le?.[0]?.id || '';
        if (defaultLe) setBranchForm((f) => ({ ...f, legalEntityId: defaultLe }));
      }
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (contextLegalEntityId) {
      setBranchForm((f) => ({ ...f, legalEntityId: contextLegalEntityId }));
    }
  }, [contextLegalEntityId]);

  const createBranch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!branchForm.legalEntityId) {
      setError('Seleccioná la empresa de la sucursal');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiService.createBranch({
        code: branchForm.code.toUpperCase(),
        name: branchForm.name,
        zone: branchForm.zone || undefined,
        legalEntityId: branchForm.legalEntityId,
      });
      setBranchForm({
        code: '',
        name: '',
        zone: '',
        legalEntityId: contextLegalEntityId || entities[0]?.id || '',
      });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 space-y-8">
      <h1 className="text-3xl font-bold text-gray-900">Sucursales y Cajas</h1>
      <p className="text-sm text-gray-600">
        ABM de sucursales. Las cajas (Central / Madre / Operativa) se crean y editan en{' '}
        <Link to="/treasury/cash-registers" className="text-blue-700 underline">
          Tesorería → Cajas
        </Link>
        . Cierre y arqueo en{' '}
        <Link to="/cash" className="text-blue-700 underline">
          Mi caja
        </Link>
        ; supervisión en{' '}
        <Link to="/treasury/supervision" className="text-blue-700 underline">
          Supervisión
        </Link>
        .
      </p>
      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      {pendingAuditsCount > 0 && (
        <div className="bg-amber-50 rounded-lg border border-amber-200 p-4 text-sm text-amber-950">
          Hay {pendingAuditsCount} cierre{pendingAuditsCount === 1 ? '' : 's'} pendiente
          {pendingAuditsCount === 1 ? '' : 's'} de arqueo.{' '}
          <Link to="/treasury/supervision?tab=pending" className="font-medium underline">
            Ir a Supervisión → Arqueos pendientes
          </Link>
        </div>
      )}

      <section className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold mb-4">Nueva sucursal</h2>
        <form onSubmit={createBranch} className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <select
            required
            value={branchForm.legalEntityId}
            onChange={(e) => setBranchForm({ ...branchForm, legalEntityId: e.target.value })}
            className="px-3 py-2 border rounded-md"
          >
            <option value="">Empresa…</option>
            {entities.map((e) => (
              <option key={e.id} value={e.id}>
                {e.legalName}
              </option>
            ))}
          </select>
          <input
            required
            placeholder="Código (VIEDMA)"
            value={branchForm.code}
            onChange={(e) => setBranchForm({ ...branchForm, code: e.target.value })}
            className="px-3 py-2 border rounded-md"
          />
          <input
            required
            placeholder="Nombre"
            value={branchForm.name}
            onChange={(e) => setBranchForm({ ...branchForm, name: e.target.value })}
            className="px-3 py-2 border rounded-md"
          />
          <input
            placeholder="Zona"
            value={branchForm.zone}
            onChange={(e) => setBranchForm({ ...branchForm, zone: e.target.value })}
            className="px-3 py-2 border rounded-md"
          />
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            Crear sucursal
          </button>
        </form>
      </section>

      <section className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold mb-2">Cajas</h2>
        <p className="text-sm text-gray-600 mb-3">
          El alta con tipo (Operativa / Madre / Central) vive en Tesorería para evitar enums técnicos
          y validar reglas por tipo.
        </p>
        <Link
          to="/treasury/cash-registers"
          className="inline-flex px-4 py-2 bg-slate-900 text-white rounded-md text-sm"
        >
          Administrar cajas en Tesorería
        </Link>
      </section>

      {/* legacy create form removed — TX8 type-first flow in /treasury/cash-registers */}

      {loading ? (
        <p className="text-gray-500">Cargando…</p>
      ) : (
        <>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Código
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Zona
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Cajas
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {branches.map((b) => (
                  <tr key={b.id}>
                    <td className="px-6 py-4 text-sm font-mono">{b.code}</td>
                    <td className="px-6 py-4 text-sm">{b.name}</td>
                    <td className="px-6 py-4 text-sm">{b.zone ?? '—'}</td>
                    <td className="px-6 py-4 text-sm">
                      {b.cashRegisters?.map((c: any) => c.number).join(', ') ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Sucursal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Caja
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Sesión abierta
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {registers.map((r) => (
                  <tr key={r.id}>
                    <td className="px-6 py-4 text-sm">{r.branch?.name}</td>
                    <td className="px-6 py-4 text-sm font-mono">{r.number}</td>
                    <td className="px-6 py-4 text-sm">
                      {r.sessions?.[0]
                        ? `${r.sessions[0].user?.firstName} ${r.sessions[0].user?.lastName}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
